import { useImportTableDefinition } from '@front/hook/Api/ImportTableDefinition';
import { useImportTask } from '@front/hook/Api/ImportTask';
import { useMemberNameById } from '@front/hook/useMemberNameById';
import { useTableDefinitionByRevisionId } from '@front/hook/useTableDefinitionByRevisionId';
import {
  FilterChip,
  List,
  ListBody,
  ListCell,
  ListHead,
  ListHeadCell,
  ListRow,
  Loader,
  Menu,
  MenuCheckBoxItem,
  MenuContent,
  MenuSeparator,
  MenuTrigger,
  ScrollArea,
} from '@plaidev/sour-react';
import { createFileRoute, type ErrorComponentProps, Link, useNavigate } from '@tanstack/react-router';
import { useProject } from 'auth-lib-front';
import { useMemo } from 'react';
import { DomainV2 } from 'troppus-common/domain';

import { TaskResultBadge } from './TaskResultBadge';

import ImportTableDefinition = DomainV2.Import.ImportTableDefinition;
import ImportTableDefinitionRevision = DomainV2.Import.ImportTableDefinitionRevision;
import ImportTask = DomainV2.Import.ImportTask;

type TasksSearch = {
  importTableDefinitionId?: string;
  importTableDefinitionRevisionId?: string;
};

export const Route = createFileRoute('/tasks/')({
  validateSearch: (search): TasksSearch => ({
    importTableDefinitionId: typeof search.importTableDefinitionId === 'string' ? search.importTableDefinitionId : undefined,
    importTableDefinitionRevisionId:
      typeof search.importTableDefinitionRevisionId === 'string' ? search.importTableDefinitionRevisionId : undefined,
  }),
  component: TasksIndexPage,
  errorComponent: TasksRouteError,
});

function TasksRouteError({ error }: ErrorComponentProps) {
  const message = error instanceof Error ? error.message : 'データの取得に失敗しました';
  return <div className="rounded-lg border border-negative bg-negative-light p-4 text-sm text-negative">{message}</div>;
}

type SummaryLines = { base: string; extracted?: string };

function summaryLines(task: ImportTask): SummaryLines | undefined {
  const s = task.resultJson?.summary;
  if (!s) return undefined;
  const base = `全${s.totalRows} / 作成${s.created} / 更新${s.updated} / スキップ${s.skipped} / 失敗${s.failed}`;
  if (s.extractedContracts == null && s.extractedCustomers == null) return { base };
  const extracted = `契約${s.extractedContracts ?? '-'} / 顧客${s.extractedCustomers ?? '-'}`;
  return { base, extracted };
}

function taskDateText(task: ImportTask): string {
  const label = task.finishedAt ? '完了' : '作成';
  const date = task.finishedAt ?? task.createdAt;
  return `${label}: ${date.format('YYYY-MM-DD HH:mm')}`;
}

function TasksIndexPage() {
  const navigate = useNavigate();
  const { project } = useProject();
  const { importTableDefinitionId: rawTableDefId, importTableDefinitionRevisionId: rawRevisionId } = Route.useSearch();

  const tableDefinitionId = useMemo(
    () => (rawTableDefId ? ImportTableDefinition.Id.FromString(rawTableDefId) : undefined),
    [rawTableDefId],
  );
  const revisionId = useMemo(
    () => (rawRevisionId ? ImportTableDefinitionRevision.Id.FromString(rawRevisionId) : undefined),
    [rawRevisionId],
  );

  const { data: tableDefinitions = [] } = useImportTableDefinition.List(project.id);
  const { data: revisions = [] } = useImportTableDefinition.ListRevisions({
    projectId: project.id,
    id: tableDefinitionId,
  });

  const {
    data: tasks,
    isPending,
    isFetching,
  } = useImportTask.List(project.id, {
    importTableDefinitionId: tableDefinitionId,
    importTableDefinitionRevisionId: revisionId,
    pollIntervalMs: 10_000,
  });
  const memberNameById = useMemberNameById(project.id);
  const { map: tableDefinitionByRevisionId, isLoading: isTableDefinitionRevisionMapLoading } = useTableDefinitionByRevisionId(project.id);

  const tableDefinitionOptions = useMemo(
    () =>
      [...tableDefinitions]
        .sort((a, b) => a.definition.displayName.localeCompare(b.definition.displayName))
        .map((d) => ({ value: d.definition.id, label: d.definition.displayName })),
    [tableDefinitions],
  );

  const revisionOptions = useMemo(
    () => [...revisions].sort((a, b) => b.revisionNumber - a.revisionNumber).map((r) => ({ value: r.id, label: `v${r.revisionNumber}` })),
    [revisions],
  );

  const tableDefinitionSelectedLabel = useMemo(() => {
    if (!rawTableDefId) return undefined;
    return tableDefinitionOptions.find((o) => o.value === rawTableDefId)?.label;
  }, [rawTableDefId, tableDefinitionOptions]);

  const revisionSelectedLabel = useMemo(() => {
    if (!rawRevisionId) return undefined;
    return revisionOptions.find((o) => o.value === rawRevisionId)?.label;
  }, [rawRevisionId, revisionOptions]);

  const handleTableDefinitionChange = (next: string | undefined) => {
    void navigate({
      to: '/tasks',
      search: () => ({
        importTableDefinitionId: next,
        importTableDefinitionRevisionId: undefined,
      }),
    });
  };

  const handleRevisionChange = (next: string | undefined) => {
    void navigate({
      to: '/tasks',
      search: (prev: TasksSearch) => ({
        importTableDefinitionId: prev.importTableDefinitionId,
        importTableDefinitionRevisionId: next,
      }),
    });
  };

  const rows = useMemo(() => {
    return [...(tasks ?? [])].sort((a, b) => b.createdAt.valueOf() - a.createdAt.valueOf());
  }, [tasks]);

  if (isPending) {
    return (
      <ScrollArea className="h-full min-w-0" orientation="vertical">
        <div className="flex justify-center p-8 py-16">
          <Loader />
        </div>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea className="h-full min-w-0" orientation="vertical">
      <div className="flex min-w-0 flex-col gap-6 p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="m-0 text-xl font-semibold leading-none text-default">タスク一覧</h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Menu>
            <MenuTrigger asChild>
              <FilterChip size="sm" className="min-w-[168px]" label="テーブル定義" selected={!!rawTableDefId} showLabel>
                {tableDefinitionSelectedLabel ?? ''}
              </FilterChip>
            </MenuTrigger>
            <MenuContent side="bottom" align="start" className="flex max-h-72 flex-col overflow-y-auto rounded-xl">
              <MenuCheckBoxItem checked={!rawTableDefId} onCheckedChange={() => handleTableDefinitionChange(undefined)}>
                <span className="min-w-0 flex-1 truncate text-left">すべて</span>
              </MenuCheckBoxItem>
              {tableDefinitionOptions.length > 0 && <MenuSeparator />}
              {tableDefinitionOptions.map((opt) => (
                <MenuCheckBoxItem //
                  key={opt.value}
                  checked={opt.value === rawTableDefId}
                  onCheckedChange={() => handleTableDefinitionChange(opt.value)}
                >
                  <span className="min-w-0 flex-1 truncate text-left">{opt.label}</span>
                </MenuCheckBoxItem>
              ))}
            </MenuContent>
          </Menu>

          {tableDefinitionId ? (
            <Menu>
              <MenuTrigger asChild>
                <FilterChip size="sm" className="min-w-[112px]" label="バージョン" selected={!!rawRevisionId} showLabel>
                  {revisionSelectedLabel ?? ''}
                </FilterChip>
              </MenuTrigger>
              <MenuContent side="bottom" align="start" className="flex flex-col rounded-xl">
                <MenuCheckBoxItem checked={!rawRevisionId} onCheckedChange={() => handleRevisionChange(undefined)}>
                  <span className="min-w-0 flex-1 truncate text-left">すべて</span>
                </MenuCheckBoxItem>
                {revisionOptions.length > 0 && <MenuSeparator />}
                {revisionOptions.map((opt) => (
                  <MenuCheckBoxItem //
                    key={opt.value}
                    checked={opt.value === rawRevisionId}
                    onCheckedChange={() => handleRevisionChange(opt.value)}
                  >
                    <span className="min-w-0 flex-1 truncate text-left">{opt.label}</span>
                  </MenuCheckBoxItem>
                ))}
              </MenuContent>
            </Menu>
          ) : (
            <FilterChip size="sm" className="min-w-[112px]" label="バージョン" disabled selected={false} />
          )}
        </div>

        {rows.length === 0 ? (
          <p className="rounded-lg border border-default bg-subtle-light py-12 text-center text-sm text-muted-light">
            該当するタスクはありません。テーブル定義を作成して CSV を取り込むとここに表示されます。
          </p>
        ) : (
          <ScrollArea className="w-full min-w-0 max-w-full" orientation="horizontal">
            <div className="w-max min-w-full">
              <List
                loader={
                  <div className="flex justify-center py-12">
                    <Loader />
                  </div>
                }
                loading={isFetching}
              >
                <ListHead>
                  <ListRow>
                    <ListHeadCell>テーブル定義</ListHeadCell>
                    <ListHeadCell width={128}>結果</ListHeadCell>
                    <ListHeadCell>件数サマリ</ListHeadCell>
                    <ListHeadCell width={170}>日時</ListHeadCell>
                    <ListHeadCell width={140}>作業者</ListHeadCell>
                  </ListRow>
                </ListHead>
                <ListBody>
                  {rows.map((task) => {
                    const matchedDefinition = tableDefinitionByRevisionId.get(task.importTableDefinitionRevisionId);
                    return (
                      <ListRow
                        key={task.id}
                        className="cursor-pointer hover:bg-action-subtle-hover"
                        onClick={() => void navigate({ to: '/tasks/$id', params: { id: task.id } })}
                      >
                        <ListCell>
                          {matchedDefinition ? (
                            <Link
                              to="/table-definitions/$id"
                              params={{ id: matchedDefinition.definition.id }}
                              className="block max-w-0 min-w-full truncate text-sm font-medium text-default underline-offset-2 transition-colors group-hover/row:text-primary group-hover/row:underline hover:text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {matchedDefinition.definition.displayName}
                            </Link>
                          ) : isTableDefinitionRevisionMapLoading ? (
                            <span className="block max-w-0 min-w-full truncate text-sm text-muted-light">読み込み中…</span>
                          ) : (
                            <span className="block max-w-0 min-w-full truncate text-sm font-medium text-default">
                              {task.importTableDefinitionRevisionId}
                            </span>
                          )}
                        </ListCell>
                        <ListCell width={128}>
                          <TaskResultBadge task={task} />
                        </ListCell>
                        <ListCell>
                          {(() => {
                            const lines = summaryLines(task);
                            if (!lines) return <span className="text-sm text-muted-light">-</span>;
                            return (
                              <div className="flex flex-col gap-0.5 text-sm text-muted-light">
                                {lines.extracted ? <span>{lines.extracted}</span> : null}
                                <span className={lines.extracted ? 'text-xs' : undefined}>{lines.base}</span>
                              </div>
                            );
                          })()}
                        </ListCell>
                        <ListCell width={170}>
                          <span className="text-sm text-muted-light">{taskDateText(task)}</span>
                        </ListCell>
                        <ListCell width={140}>
                          <span className="block max-w-0 min-w-full truncate text-sm text-muted-light">
                            {memberNameById.get(task.createdBy) ?? task.createdBy}
                          </span>
                        </ListCell>
                      </ListRow>
                    );
                  })}
                </ListBody>
              </List>
            </div>
          </ScrollArea>
        )}
      </div>
    </ScrollArea>
  );
}
